import { useId, useState } from 'react';
import { ApiError } from '@awj/api-client';
import { Button, TextField } from '@awj/ui';

import { Icon } from './Icon.tsx';
import { LocaleSwitch } from './LocaleSwitch.tsx';
import { useT } from '~/i18n/index.tsx';
import { useSession } from '~/lib/session.tsx';
import { STOREFRONT_URL } from '~/lib/storefront.ts';

/**
 * The way in.
 *
 * Shown in place of the whole application rather than at a route of its own, so there is no
 * signed-out URL to be redirected to and back from — and nothing to get wrong about where to
 * return afterwards.
 */
export function SignIn({ reason }: { reason?: 'notStaff' }) {
  const t = useT();
  const { signIn, signOut } = useSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [attemptError, setAttemptError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * Derived, not seeded into state.
   *
   * `reason` appears *after* this component has mounted — a customer signs in successfully, the
   * gate re-renders, and the same instance is handed `reason="notStaff"`. A `useState` initialiser
   * does not run again for that, so the explanation never appeared and the form simply seemed to
   * reject a password it had just accepted.
   *
   * The attempt's own error takes precedence once there is one, so a subsequent wrong password
   * replaces the message rather than hiding behind it.
   */
  const error = attemptError ?? (reason === 'notStaff' ? t('auth.notStaff') : null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    // Said here, in the panel's language. Sent empty, the API's refusal was ASP.NET's own "One or
    // more validation errors occurred." — in English, on the Arabic panel too.
    if (!email.trim() || !password) {
      setAttemptError(t('auth.missingFields'));
      return;
    }

    setBusy(true);
    setAttemptError(null);

    try {
      // A customer signed in on the shop reaching this URL is signed out first, so the next
      // attempt starts from a clean session rather than layering one over the other.
      if (reason === 'notStaff') {
        await signOut();
      }

      await signIn(email.trim(), password);
    } catch (caught) {
      setAttemptError(signInError(caught, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      {/*
        The plate: one picture, edge to edge.

        The artwork carries the lockup itself, so nothing is drawn over it — no wordmark, no copy,
        no scrim. It is decorative in the strict sense: the form beside it carries the whole task,
        and the app's name reaches a screen reader from the lockup in the card.
      */}
      <aside className="signin__plate" aria-hidden="true" />

      <div className="signin__side">
        <div className="signin__panel">
          {/*
            The card's own head: the way out of a language you cannot read, and the lockup. The
            switch leads it deliberately — someone who cannot read this screen has to be able to
            find that control without reading anything.
          */}
          <div className="signin__head">
            <LocaleSwitch />
            <span
              className="admin__wordmark admin__wordmark--ink"
              role="img"
              aria-label={t('app.title')}
            />
          </div>

          <div className="signin__intro">
            <h1 className="signin__title">{t('auth.signIn')}</h1>
            <p className="signin__lede">{t('auth.lede')}</p>
          </div>

          <form className="signin__form" onSubmit={submit} noValidate>
            <TextField
              label={t('auth.email')}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <PasswordField
              label={t('auth.password')}
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
            />

            {error ? (
              <p className="signin__error" role="alert">
                {error}
              </p>
            ) : null}

            <Button type="submit" size="lg" block loading={busy} loadingLabel={t('auth.signingIn')}>
              {t('auth.signIn')}
            </Button>
          </form>

          {/*
            The one control on this screen that LEAVES the panel, so it is drawn as a departure
            rather than as a link: a quiet capsule with an arrow that travels outward on approach.

            It opens in a new tab — someone checking the shop mid-sign-in should not lose the
            credentials they have half-typed — and says so, because a link that moves you to a new
            tab without warning is disorienting for anyone not watching the tab strip.
          */}
          <div className="signin__foot">
            <a
              className="signin__storefront"
              href={STOREFRONT_URL}
              target="_blank"
              rel="noreferrer"
            >
              <span className="signin__storefrontMark" aria-hidden="true">
                <Icon name="external" size={15} />
              </span>
              {t('auth.storefront')}
              <span className="sr-only"> ({t('common.opensInNewTab')})</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * What to say when signing in fails, in the language the panel is set to.
 *
 * Translated from the API's error CODE rather than shown as the API's own sentence: those are
 * English only, so a panel set to Arabic was refusing people in English.
 *
 * The property the API is careful about survives it. `auth.invalid_credentials` is deliberately
 * the one code for a wrong password and for an address with no account, so both still produce the
 * same words — this form cannot be used to discover which addresses belong to staff, which is a
 * shorter list than the customers'.
 */
function signInError(caught: unknown, t: ReturnType<typeof useT>): string {
  if (!(caught instanceof ApiError)) {
    return t('common.somethingWentWrong');
  }

  switch (caught.code) {
    case 'auth.invalid_credentials':
      return t('auth.failed');
    case 'auth.email_not_confirmed':
      return t('auth.emailNotConfirmed');
    case 'auth.locked_out':
      return t('auth.lockedOut');
    case 'auth.account_blocked':
      return t('auth.blocked');
    default:
      // A malformed address fails validation rather than sign-in; to the person typing it, it is
      // the same thing, and the API's English sentence about it is not.
      return caught.status === 400 ? t('auth.failed') : (caught.problem.detail ?? t('auth.failed'));
  }
}

/**
 * The password, with a way to see what was typed.
 *
 * Kept to the kit's own field markup so it lines up with the email above it; the only addition is
 * the eye at the field's reading end. It is a toggle, not a hold-to-peek — a press that has to be
 * held is out of reach of anyone who cannot hold one — and its name says what it will do next.
 */
function PasswordField({
  label,
  autoComplete,
  value,
  onChange,
}: {
  label: string;
  autoComplete: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const t = useT();
  const id = useId();
  const [shown, setShown] = useState(false);

  return (
    <div className="awj-field">
      <label className="awj-field__label" htmlFor={id}>
        {label}
      </label>
      <div className="field-reveal">
        <input
          id={id}
          className="awj-input field-reveal__input"
          type={shown ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
        />
        <button
          type="button"
          className="field-reveal__toggle"
          aria-label={shown ? t('auth.hidePassword') : t('auth.showPassword')}
          aria-pressed={shown}
          aria-controls={id}
          onClick={() => setShown((current) => !current)}
        >
          <Icon name={shown ? 'eyeOff' : 'eye'} size={18} />
        </button>
      </div>
    </div>
  );
}
